import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment-timezone';
import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { toast } from 'sonner';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { getDisplayName, getPrincipalInitials } from '../../../../../models/NameUtils';
import { inFlightRunStatusText, useRunEmailExecBrief } from '../../../../../hooks/useRunEmailExecBrief';
import {
	selectBoundEmailExecTemplate,
	updateEmailExecAssistantTemplatesRQCache,
	useEmailExecAssistantTemplatesSuspenseQuery,
} from '../../../../../queries/EmailExecAssistant/useEmailExecTemplates';
import { useLatestEmailExecDigestQuery } from '../../../../../queries/EmailExecAssistant/useLatestEmailExecDigestQuery';
import { Button } from '../../../../components-v2/Button';
import { Checkbox } from '../../../../components-v2/Checkbox';
import {
	Combobox,
	ComboboxChip,
	ComboboxChipRemove,
	ComboboxChips,
	ComboboxChipsInput,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemIndicator,
	ComboboxList,
	ComboboxValue,
	useComboboxAnchor,
} from '../../../../components-v2/Combobox';
import {
	ComboboxAvatarItem,
	ComboboxAvatarItemAvatar,
	ComboboxAvatarItemDescription,
	ComboboxAvatarItemTitle,
} from '../../../../components-v2/Combobox/ComboboxAvatarItem';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { RadioGroup, RadioGroupItem } from '../../../../components-v2/RadioGroup';
import { useLoadAllUsers } from '../../../../../queries/User/useLoadAllUsers';
import { ROUTES } from '../../../../constants/routes';
import { MAX_IGNORE_NOISE_NOTES_LENGTH, MAX_KEYWORDS } from './constants';
import {
	canScheduleEmailExec,
	DAY_OF_WEEK_NAMES,
	DEFAULT_SCHEDULE_DAY_INDEXES,
	getDefaultScheduleMinute,
	getScheduleTimeOptions,
	getScheduleTimezone,
	getTimezoneLabel,
	getUtcOffsetMinutes,
	localScheduleToUtcSlots,
	type ScheduleTimeOption,
	utcSlotsToLocalSchedule,
} from './scheduleUtils';
import {
	isEligibleMailboxUser,
	isMailboxSelectionValid,
	mailboxAddress,
	mailboxDisplayName,
	mailboxPickerItems,
	mailboxSearchText,
	mailboxSourcesForSave,
	MAX_SOURCE_MAILBOXES,
	sameMailboxSelection,
	selectedMailboxUsers,
	templateToMailboxSelection,
} from './mailboxUtils';
import { styleSheet } from './styles';
import { addKeywords, splitKeywordInput } from './utils';
import { Field, FieldDescription, FieldError, FieldLabel } from '@HoudiniUI/Field';
import { InputGroupText } from '@HoudiniUI/InputGroup';
import { AIPromptBoxFooter } from '@HoudiniUI/ai/AIPromptBox';
import { Label } from '@HoudiniUI/Label';
import { PlayIcon } from '@WebComponents/svgs/icons/PlayIcon';
import { useCreateEmailExecAssistantTemplateMutation } from '../../../../../queries/EmailExecAssistant/useCreateEmailExecAssistantTemplateMutation';
import { useUpdateEmailExecAssistantTemplateMutation } from '../../../../../queries/EmailExecAssistant/useUpdateEmailExecAssistantTemplateMutation';
import { Input } from '../../../../components-v2/Input';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { RotateCCWClockIcon } from '../../../svgs/icons/RotateCCWClockIcon';
import { AIPromptUsage } from '../../../../../models/AIPromptTemplate';
import { AIPromptField, useAIPromptField } from '../../../../../shared/components/ai/AIPromptField';

/** The user-editable subset of the template; everything else is server-managed and round-tripped. */
interface EditableFields {
	name: string;
	objectivePrompt: string;
	monitoredKeywords: string[];
	ignoreNoiseNotes: string;
	scheduleEnabled: boolean;
	scheduleDayIndexes: number[];
	scheduleMinuteOfDay: number;
	mailboxScope: 'self' | 'all' | 'named';
	mailboxUserIds: string[];
}

const sameKeywords = (a: string[], b: string[]): boolean =>
	a.length === b.length && a.every((value, index) => value === b[index]);

const sameDayIndexes = (a: number[], b: number[]): boolean =>
	a.length === b.length && a.every((value, index) => value === b[index]);

const seedScheduleFields = (
	template: Api.EmailExecAssistantTemplate | null,
	offsetMinutes: number,
	defaultMinuteOfDay: number
): Pick<EditableFields, 'scheduleEnabled' | 'scheduleDayIndexes' | 'scheduleMinuteOfDay'> => {
	const stored = utcSlotsToLocalSchedule(template?.scheduleSlots, offsetMinutes);
	return {
		scheduleEnabled: !!template?.scheduleEnabled,
		scheduleDayIndexes: stored?.dayIndexes ?? [...DEFAULT_SCHEDULE_DAY_INDEXES],
		scheduleMinuteOfDay: stored?.minuteOfDay ?? defaultMinuteOfDay,
	};
};

const templateToEditableFields = (
	template: Api.EmailExecAssistantTemplate,
	offsetMinutes: number,
	defaultMinuteOfDay: number
): EditableFields => {
	const mailboxes = templateToMailboxSelection(template);
	return {
		name: template.name ?? '',
		objectivePrompt: template.objectivePrompt ?? '',
		monitoredKeywords: template.monitoredKeywords ?? [],
		ignoreNoiseNotes: template.ignoreNoiseNotes ?? '',
		mailboxScope: mailboxes.scope,
		mailboxUserIds: mailboxes.userIds,
		...seedScheduleFields(template, offsetMinutes, defaultMinuteOfDay),
	};
};

const initialEditableFields = (name: string, offsetMinutes: number, defaultMinuteOfDay: number): EditableFields => ({
	name: name ? `Exec Assistant for ${name}` : '',
	objectivePrompt: '',
	monitoredKeywords: [],
	ignoreNoiseNotes: '',
	mailboxScope: 'self',
	mailboxUserIds: [],
	...seedScheduleFields(null, offsetMinutes, defaultMinuteOfDay),
});

export const EmailExecAssistantSettingsContent: React.FC = () => {
	const id = React.useId();
	const userSession = useUserSession();
	const { logEvent, logApiError } = useEventLogging('EmailExecAssistantSettings');

	const timeZone = getScheduleTimezone(userSession.user);
	const offsetMinutes = React.useMemo(() => getUtcOffsetMinutes(timeZone), [timeZone]);
	const timeOptions = React.useMemo(() => getScheduleTimeOptions(offsetMinutes), [offsetMinutes]);
	const defaultScheduleMinute = React.useMemo(() => getDefaultScheduleMinute(timeOptions), [timeOptions]);
	const scheduleEligible = canScheduleEmailExec(userSession.user);
	const canConfigureMailboxes = userSession.userRole === Api.EUserRole.SuperAdmin;
	const mailboxAnchor = useComboboxAnchor();
	const { users: accountUsers } = useLoadAllUsers({ enabled: canConfigureMailboxes });

	const { data: templates } = useEmailExecAssistantTemplatesSuspenseQuery({
		meta: { toastErrorMessage: 'Failed to load your Email Exec Assistant settings.' },
		staleTime: Infinity,
	});
	const latestDigestQuery = useLatestEmailExecDigestQuery();
	// The full server template we round-trip on save (PUT is a whole-object replace); null until created.
	const loadedTemplate = selectBoundEmailExecTemplate(templates) ?? null;
	// `baseline` is the last-saved editable state — the reference for dirty-tracking and Discard.
	const baseline = templates?.length
		? templateToEditableFields(loadedTemplate, offsetMinutes, defaultScheduleMinute)
		: initialEditableFields(getDisplayName(userSession.user), offsetMinutes, defaultScheduleMinute);

	const [form, setForm] = React.useState<EditableFields>(
		templates?.length
			? templateToEditableFields(loadedTemplate, offsetMinutes, defaultScheduleMinute)
			: initialEditableFields(getDisplayName(userSession.user), offsetMinutes, defaultScheduleMinute)
	);
	const [keywordInput, setKeywordInput] = React.useState('');

	// Telemetry carries counts/booleans only — never keyword/prompt content (sensitive inbox data).
	const savedTemplateEventPayload = (template: Api.EmailExecAssistantTemplate, saved: EditableFields) => ({
		isCreate: !loadedTemplate?.id,
		keywordCount: saved.monitoredKeywords.length,
		hasNoiseNotes: saved.ignoreNoiseNotes.length > 0,
		scheduleEnabled: !!template.scheduleEnabled,
		scheduleDayCount: template.scheduleSlots?.length ?? 0,
		scheduleHourLocal: Math.floor(saved.scheduleMinuteOfDay / 60),
		scheduleMinuteLocal: saved.scheduleMinuteOfDay % 60,
	});
	const updateEmailExecAssistantMutation = useUpdateEmailExecAssistantTemplateMutation({
		onSuccess: template => {
			const saved = templateToEditableFields(template, offsetMinutes, defaultScheduleMinute);
			logEvent('EmailExecTemplateUpdated', savedTemplateEventPayload(template, saved));
			updateEmailExecAssistantTemplatesRQCache(template);
			setForm(saved);
			toast.success('Email Exec Assistant settings saved.');
		},
		onError: error => {
			logApiError('EmailExecTemplateUpdate-Error', error);
			toast.error('Problem occurred saving your settings.');
		},
	});
	const createEmailExecAssistantMutation = useCreateEmailExecAssistantTemplateMutation({
		onSuccess: template => {
			const saved = templateToEditableFields(template, offsetMinutes, defaultScheduleMinute);
			logEvent('EmailExecTemplateCreated', savedTemplateEventPayload(template, saved));
			updateEmailExecAssistantTemplatesRQCache(template);
			setForm(saved);
			toast.success('Email Exec Assistant settings saved.');
		},
		onError: error => {
			logApiError('EmailExecTemplateCreate-Error', error);
			toast.error('Problem occurred saving your settings.');
		},
	});

	const { runBrief, isRunning, inFlightRun } = useRunEmailExecBrief({ source: 'settings' });

	const formMailboxes = { scope: form.mailboxScope, userIds: form.mailboxUserIds };
	const storedMailboxes = templateToMailboxSelection(loadedTemplate);
	const mailboxItems = mailboxPickerItems(accountUsers, form.mailboxUserIds, userSession.user?.id);

	const isDirty =
		form.name !== baseline.name ||
		form.objectivePrompt !== baseline.objectivePrompt ||
		form.ignoreNoiseNotes !== baseline.ignoreNoiseNotes ||
		!sameKeywords(form.monitoredKeywords, baseline.monitoredKeywords) ||
		form.scheduleEnabled !== baseline.scheduleEnabled ||
		form.scheduleMinuteOfDay !== baseline.scheduleMinuteOfDay ||
		!sameDayIndexes(form.scheduleDayIndexes, baseline.scheduleDayIndexes) ||
		(canConfigureMailboxes && !sameMailboxSelection(formMailboxes, storedMailboxes));
	const updateField = <TKey extends keyof EditableFields>(key: TKey, value: EditableFields[TKey]) => {
		setForm(prev => ({ ...prev, [key]: value }));
	};
	const objectiveField = useAIPromptField({
		onChange: value => updateField('objectivePrompt', value),
		value: form.objectivePrompt,
	});
	const ignoreField = useAIPromptField({
		onChange: value => updateField('ignoreNoiseNotes', value.slice(0, MAX_IGNORE_NOISE_NOTES_LENGTH)),
		value: form.ignoreNoiseNotes,
	});

	const isSaving = createEmailExecAssistantMutation.isPending || updateEmailExecAssistantMutation.isPending;
	const scheduleValid = !form.scheduleEnabled || form.scheduleDayIndexes.length > 0;
	const mailboxesValid = !canConfigureMailboxes || isMailboxSelectionValid(formMailboxes);
	const canSave =
		isDirty &&
		scheduleValid &&
		mailboxesValid &&
		form.objectivePrompt.trim().length > 0 &&
		form.name.trim().length > 0 &&
		!objectiveField.unfilledPlaceholder &&
		!ignoreField.unfilledPlaceholder &&
		!isSaving &&
		!isRunning;
	const canRunNow = !!loadedTemplate?.id && !isDirty && !isRunning && !isSaving;

	const onToggleScheduleDay = (dayIndex: number, checked: boolean) => {
		setForm(prev => ({
			...prev,
			scheduleDayIndexes: checked
				? [...new Set([...prev.scheduleDayIndexes, dayIndex])].sort((a, b) => a - b)
				: prev.scheduleDayIndexes.filter(day => day !== dayIndex),
		}));
	};

	const onMailboxUsersChange = (users: Api.IUser[]) => {
		setForm(prev => ({ ...prev, mailboxUserIds: users.slice(0, MAX_SOURCE_MAILBOXES).map(user => user.id) }));
	};

	const scheduleFieldsForSave = (): Partial<
		Pick<Api.EmailExecAssistantTemplate, 'scheduleEnabled' | 'scheduleSlots'>
	> => {
		if (!scheduleEligible) {
			return {};
		}
		if (!form.scheduleEnabled && !loadedTemplate?.scheduleSlots?.length) {
			return { scheduleEnabled: false };
		}
		return {
			scheduleEnabled: form.scheduleEnabled,
			scheduleSlots: localScheduleToUtcSlots(
				{ dayIndexes: form.scheduleDayIndexes, minuteOfDay: form.scheduleMinuteOfDay },
				offsetMinutes
			),
		};
	};

	const onKeywordKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key !== 'Enter' && event.key !== ',') {
			return;
		}
		// Straight-add: commit the typed text as chip(s) on Enter/comma — no dropdown item, no dialog.
		event.preventDefault();
		const additions = splitKeywordInput(keywordInput);
		if (additions.length === 0) {
			return;
		}
		setForm(prev => ({ ...prev, monitoredKeywords: addKeywords(prev.monitoredKeywords, additions) }));
		setKeywordInput('');
	};

	const onSave = () => {
		if (!canSave) {
			return;
		}
		if (loadedTemplate?.id) {
			updateEmailExecAssistantMutation.mutate({
				...loadedTemplate,
				name: form.name.trim(),
				objectivePrompt: form.objectivePrompt.trim(),
				monitoredKeywords: form.monitoredKeywords,
				ignoreNoiseNotes: form.ignoreNoiseNotes.trim() || undefined,
				...scheduleFieldsForSave(),
				...mailboxSourcesForSave({
					canConfigure: canConfigureMailboxes,
					selection: formMailboxes,
					stored: storedMailboxes,
				}),
			});
		} else {
			createEmailExecAssistantMutation.mutate({
				name: form.name.trim(),
				objectivePrompt: form.objectivePrompt.trim(),
				monitoredKeywords: form.monitoredKeywords,
				ignoreNoiseNotes: form.ignoreNoiseNotes.trim() || undefined,
				isActive: true,
				...scheduleFieldsForSave(),
				...mailboxSourcesForSave({
					canConfigure: canConfigureMailboxes,
					selection: formMailboxes,
					stored: storedMailboxes,
				}),
			});
		}
	};

	const onDiscard = () => {
		setForm(baseline);
		setKeywordInput('');
	};

	const onRunNow = () => {
		if (!canRunNow || !loadedTemplate?.id) {
			return;
		}
		runBrief(loadedTemplate.id);
	};

	const selectedTimeOption = timeOptions.find(option => option.minuteOfDay === form.scheduleMinuteOfDay) ?? null;
	const timezoneLabel = getTimezoneLabel(timeZone);
	const lastBriefDate = latestDigestQuery.data?.runDate;

	return (
		<div className={css(styleSheet.root)}>
			<div className={css(styleSheet.header)}>
				<Button variant='link' asChild>
					<NavLink to={ROUTES.emailExec.briefs}>
						<RotateCCWClockIcon />
						View brief history
					</NavLink>
				</Button>
				<div className={css(styleSheet.runNowContainer)}>
					<Button onClick={onRunNow} isLoading={isRunning} disabled={!canRunNow}>
						<PlayIcon /> Run now
					</Button>
					{isRunning ? (
						<span className={css(styleSheet.helpText)}>
							{inFlightRun ? inFlightRunStatusText(inFlightRun) : 'Queued…'}
						</span>
					) : !loadedTemplate?.id ? (
						<span className={css(styleSheet.helpText)}>Save your settings to enable Run now.</span>
					) : isDirty ? (
						<span className={css(styleSheet.helpText)}>Save your changes to run a brief with them.</span>
					) : null}
				</div>
			</div>
			<Field>
				<FieldLabel htmlFor={`${id}-name`}>
					Assistant name <span className={css(styleSheet.requiredIndicator)}>*</span>
				</FieldLabel>
				<Input
					id={`${id}-name`}
					value={form.name}
					onChange={event => updateField('name', event.target.value)}
					required
				/>
			</Field>
			<Field>
				<FieldLabel htmlFor='email-exec-objective'>
					What should your assistant prioritize? <span className={css(styleSheet.requiredIndicator)}>*</span>
				</FieldLabel>
				<AIPromptField
					field={objectiveField}
					footer={
						<AIPromptBoxFooter>
							<InputGroupText styles={[styleSheet.helpText]}>
								{form.objectivePrompt.trim().length > 0
									? 'Describe the clients, topics, or situations you want watched and flagged.'
									: 'Tell the assistant what to prioritize before saving.'}
							</InputGroupText>
						</AIPromptBoxFooter>
					}
					id='email-exec-objective'
					placeholder='Describe what matters in your inbox — the clients, topics, or situations you want watched and flagged. e.g. “Surface anything about tax-loss harvesting, Roth conversions, or money movement for my top clients.”'
					textAreaStyles={[styleSheet.textArea]}
					usage={AIPromptUsage.EmailExecAssistant}
				/>
			</Field>

			<Field>
				<FieldLabel htmlFor={`${id}-keywords`}>Always-flag keywords</FieldLabel>
				<Combobox
					multiple
					value={form.monitoredKeywords}
					onValueChange={value => updateField('monitoredKeywords', value as string[])}
					inputValue={keywordInput}
					onInputValueChange={setKeywordInput}
				>
					<ComboboxChips>
						<ComboboxValue>
							{(keywords: string[]) => (
								<>
									{keywords.map(keyword => (
										<ComboboxChip key={keyword} aria-label={keyword}>
											{keyword}
											<ComboboxChipRemove />
										</ComboboxChip>
									))}
									<ComboboxChipsInput
										id={`${id}-keywords`}
										placeholder={keywords.length > 0 ? '' : 'Type a keyword and press Enter'}
										onKeyDown={onKeywordKeyDown}
									/>
								</>
							)}
						</ComboboxValue>
					</ComboboxChips>
				</Combobox>
				<FieldDescription>
					Emails containing any of these are always surfaced as action-needed, even if nothing else stands out.
				</FieldDescription>
				<FieldDescription>
					{form.monitoredKeywords.length}/{MAX_KEYWORDS}
				</FieldDescription>
			</Field>

			<Field>
				<FieldLabel htmlFor='email-exec-noise'>What to ignore</FieldLabel>
				<AIPromptField
					field={ignoreField}
					footer={
						<AIPromptBoxFooter>
							<InputGroupText styles={[styleSheet.helpText]}>
								{form.ignoreNoiseNotes.length}/{MAX_IGNORE_NOISE_NOTES_LENGTH}
							</InputGroupText>
						</AIPromptBoxFooter>
					}
					id='email-exec-noise'
					maxLength={MAX_IGNORE_NOISE_NOTES_LENGTH}
					placeholder='Describe the noise you don’t want in your brief. e.g. “Ignore wholesaler pitches, fixed-income solicitations, and conference invitations.”'
					textAreaStyles={[styleSheet.textArea]}
					usage={AIPromptUsage.EmailExecAssistantIgnore}
				/>
			</Field>

			{canConfigureMailboxes ? (
				<Field>
					<FieldLabel>Mailboxes this brief covers</FieldLabel>
					<RadioGroup
						value={form.mailboxScope}
						onValueChange={value => updateField('mailboxScope', value as EditableFields['mailboxScope'])}
						styles={[styleSheet.mailboxScopeOptions]}
					>
						<Label styles={[styleSheet.mailboxScopeOption]} htmlFor={`${id}-mailbox-self`}>
							<RadioGroupItem id={`${id}-mailbox-self`} value='self' />
							Only my mailbox
						</Label>
						<Label styles={[styleSheet.mailboxScopeOption]} htmlFor={`${id}-mailbox-all`}>
							<RadioGroupItem id={`${id}-mailbox-all`} value='all' />
							Everyone on the account (up to {MAX_SOURCE_MAILBOXES} mailboxes)
						</Label>
						<Label styles={[styleSheet.mailboxScopeOption]} htmlFor={`${id}-mailbox-named`}>
							<RadioGroupItem id={`${id}-mailbox-named`} value='named' />
							Choose people
						</Label>
					</RadioGroup>
					{form.mailboxScope === 'named' ? (
						<>
							<Combobox
								items={mailboxItems}
								multiple
								value={selectedMailboxUsers(accountUsers, form.mailboxUserIds)}
								itemToStringLabel={user => mailboxSearchText(user as Api.IUser)}
								isItemEqualToValue={(a, b) => (a as Api.IUser).id === (b as Api.IUser).id}
								onValueChange={value => onMailboxUsersChange(value as Api.IUser[])}
							>
								<ComboboxChips ref={mailboxAnchor}>
									<ComboboxValue>
										{(value: Api.IUser[]) => (
											<>
												{value.map(user => (
													<ComboboxChip key={user.id} aria-label={mailboxDisplayName(user)}>
														{isEligibleMailboxUser(user, userSession.user?.id)
															? mailboxDisplayName(user)
															: `${mailboxDisplayName(user)} — unavailable`}
														<ComboboxChipRemove />
													</ComboboxChip>
												))}
												<ComboboxChipsInput
													id={`${id}-mailbox-users`}
													placeholder={value.length > 0 ? '' : 'Search teammates'}
												/>
											</>
										)}
									</ComboboxValue>
								</ComboboxChips>
								<ComboboxContent anchor={mailboxAnchor}>
									<ComboboxEmpty>No teammates with a readable mailbox</ComboboxEmpty>
									<ComboboxList>
										{(user: Api.IUser) => {
											const name = mailboxDisplayName(user);
											return (
												<ComboboxAvatarItem key={user.id} value={user}>
													<ComboboxAvatarItemAvatar
														imageUrl={user.profilePic}
														fallbackText={getPrincipalInitials(user)}
													/>
													<ComboboxAvatarItemTitle>
														{isEligibleMailboxUser(user, userSession.user?.id) ? name : `${name} — unavailable`}
													</ComboboxAvatarItemTitle>
													<ComboboxAvatarItemDescription>{mailboxAddress(user)}</ComboboxAvatarItemDescription>
												</ComboboxAvatarItem>
											);
										}}
									</ComboboxList>
								</ComboboxContent>
							</Combobox>
							<FieldDescription>
								{form.mailboxUserIds.length}/{MAX_SOURCE_MAILBOXES}
							</FieldDescription>
							{form.mailboxUserIds.length === 0 ? <FieldError>Choose at least one teammate.</FieldError> : null}
						</>
					) : null}
					<FieldDescription>
						Personal email in a teammate’s inbox is never included, even when it matches one of your keywords. Your own
						inbox is unchanged.
					</FieldDescription>
				</Field>
			) : null}

			<Field>
				<Field orientation='horizontal'>
					<Checkbox
						id={`${id}-schedule-enabled`}
						checked={form.scheduleEnabled}
						disabled={!scheduleEligible}
						onCheckedChange={checked => updateField('scheduleEnabled', checked === true)}
					/>
					<FieldLabel htmlFor={`${id}-schedule-enabled`}>Email me my brief on a schedule</FieldLabel>
				</Field>
				{!scheduleEligible ? (
					<FieldDescription>
						Scheduled briefs require a Gmail, Microsoft 365, Yahoo, or AOL email connection.
					</FieldDescription>
				) : form.scheduleEnabled ? (
					<div className={css(styleSheet.scheduleFields)}>
						<div className={css(styleSheet.scheduleDaysRow)}>
							Send on
							{DAY_OF_WEEK_NAMES.map((dayName, dayIndex) => (
								<Label key={dayName} styles={[styleSheet.scheduleDay]} htmlFor={`${id}-schedule-day-${dayIndex}`}>
									<Checkbox
										id={`${id}-schedule-day-${dayIndex}`}
										checked={form.scheduleDayIndexes.includes(dayIndex)}
										onCheckedChange={checked => onToggleScheduleDay(dayIndex, checked === true)}
									/>
									{dayName.slice(0, 3)}
								</Label>
							))}
							at
							<div className={css(styleSheet.scheduleTimeRow)}>
								<Combobox
									items={timeOptions}
									value={selectedTimeOption}
									onValueChange={item => {
										const option = item as ScheduleTimeOption | null;
										if (option) {
											updateField('scheduleMinuteOfDay', option.minuteOfDay);
										}
									}}
									itemToStringLabel={item => (item as ScheduleTimeOption).label}
									isItemEqualToValue={(a, b) =>
										(a as ScheduleTimeOption).minuteOfDay === (b as ScheduleTimeOption).minuteOfDay
									}
								>
									<ComboboxInput aria-label='Delivery time' styles={[styleSheet.timeComboboxInput]} />
									<ComboboxContent>
										<ComboboxEmpty>No matching times</ComboboxEmpty>
										<ComboboxList>
											{(item: ScheduleTimeOption) => (
												<ComboboxItem key={item.minuteOfDay} value={item}>
													<ComboboxItemIndicator />
													{item.label}
												</ComboboxItem>
											)}
										</ComboboxList>
									</ComboboxContent>
								</Combobox>
								<span className={css(styleSheet.helpText)}>{timezoneLabel}</span>
							</div>
						</div>
						{form.scheduleDayIndexes.length === 0 ? <FieldError>Choose at least one day.</FieldError> : null}
					</div>
				) : null}
				{lastBriefDate ? (
					<span className={css(styleSheet.statusLine)}>
						Last brief sent {moment(lastBriefDate).tz(timeZone).format('MMM D, YYYY [at] h:mm A')}
					</span>
				) : null}
			</Field>

			<div className={css(styleSheet.footer)}>
				<Button
					onClick={onSave}
					isLoading={createEmailExecAssistantMutation.isPending || updateEmailExecAssistantMutation.isPending}
					disabled={!canSave}
				>
					Save
				</Button>
				{isDirty ? (
					<Button
						variant='secondary'
						onClick={onDiscard}
						disabled={createEmailExecAssistantMutation.isPending || updateEmailExecAssistantMutation.isPending}
					>
						Discard changes
					</Button>
				) : null}
			</div>
		</div>
	);
};

export const EmailExecAssistantSettings: React.FC = () => {
	const userSession = useUserSession();

	// MUST stay outside Suspense: useSuspenseQuery can't be disabled, so we gate by not rendering Content.
	if (!userSession.user?.emailProviderFeatures?.email?.canViewSentMessages) {
		return (
			<div className={css(styleSheet.root)}>
				<span className={css(styleSheet.statusLine)}>
					The Email Exec Assistant needs an inbox it can read. Your current email connection is send-only, so it can’t
					be used here. Connect an account with full mailbox access (Gmail, Microsoft 365, Exchange, Yahoo, AOL, or
					IMAP) to enable it.
				</span>
			</div>
		);
	}

	return (
		<QueryErrorResetBoundary>
			{({ reset }) => (
				<ErrorBoundary
					onReset={reset}
					fallbackRender={({ resetErrorBoundary }) => (
						<div className={css(styleSheet.root)}>
							<span className={css(styleSheet.statusLine)}>We couldn’t load your Email Exec Assistant settings.</span>
							<Button variant='outline' onClick={() => resetErrorBoundary()}>
								Try again
							</Button>
						</div>
					)}
				>
					<React.Suspense fallback={<LoadingSpinner type='small' />}>
						<EmailExecAssistantSettingsContent />
					</React.Suspense>
				</ErrorBoundary>
			)}
		</QueryErrorResetBoundary>
	);
};
